import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import __ from "web/Layout/Translations";

import CheckIcon from "web/assets/icons/check.svg";

import useCodeItemStatus from "web/hooks/useCodeItemStatus";
import { inactiveStatusCodes } from "web/hooks/useCodeItemStatus/useCodeItemStatus";
import usePaginationParams from "web/hooks/usePaginationParams";
import useUrlParams from "web/hooks/useUrlParams";

import orderStateStatus from "web/constants/orderStateStatus";

import { ProductCodeFrontendStatus } from "web/types/Code";
import { OrderStatus } from "web/types/Order";

import { useAppContext } from "web/context/app";
import { getCodes, setCodeUsed } from "web/features/codes/codesThunks";
import { FilterValues } from "web/features/codes/utils/helpers";

import Content from "./Content";
import Image from "./Image";
import Loader from "./Loader";
import ShowCode from "./ShowCode";
import classesMobile from "./codeItemBase.scss";
import classesDesktop from "./codeItemDesktop.scss";

const classesInit = () => {
  const { isMobile } = useAppContext();

  return isMobile ? classesMobile : classesDesktop;
};

interface ICodeItemProps {
  link?: string;
  name: string;
  itemId: number;
  image?: string;
  userUsed?: boolean | number;
  userUsedDate?: string;
  expirationDate: string;
  options?: { attribute?: { wartosc?: string } }[];
  state?: string;
  dataT1?: string;
  codeDetails?: any;
  productCodeFrontendStatus: ProductCodeFrontendStatus;
  orderStatusCode?: OrderStatus | undefined;
  producSuperOptions?: any;
  sku: string;
  codeAttributeListSize?: number;
  additionalClassName?: string;
}

const CodeItem = ({
  link = "",
  name,
  image = "",
  userUsed = 0,
  userUsedDate = "",
  expirationDate,
  options = [],
  itemId,
  state = "",
  orderStatusCode = "" as OrderStatus,
  dataT1 = "code_item",
  codeDetails = {},
  productCodeFrontendStatus,
  producSuperOptions = {},
  sku = "",
  codeAttributeListSize,
  additionalClassName,
}: ICodeItemProps) => {
  const dispatch = useDispatch();
  const { settingMarkIds = [] } = useSelector((state) => state.codes);
  const classes = classesInit();
  const [isAvailable, setAvailability] = useState(!userUsed);
  const [isAvailabilityManuallyChanged, setIsAvailabilityManuallyChanged] =
    useState(false);

  const isSettingMark = settingMarkIds.some((id) => id === itemId);

  const { isCanceled, statusCode, disabled, label } = useCodeItemStatus(
    productCodeFrontendStatus
  );
  const { page } = usePaginationParams();
  const { availability, codeName } = useUrlParams("availability", "codeName");

  const availabilityCondition = isAvailabilityManuallyChanged
    ? isAvailable
    : !inactiveStatusCodes.includes(statusCode);

  const rootClassName = availabilityCondition
    ? classes.rootAvailable
    : classes.rootInactive;

  const markAsUnused = useCallback(async () => {
    await dispatch(setCodeUsed({ itemId, isUsed: 0 }));
    dispatch(
      getCodes({
        page,
        filterValue: availability as FilterValues,
        searchName: codeName ?? "",
      })
    );
    setAvailability(true);
  }, [dispatch, itemId, isAvailable, page, availability, codeName]);

  const onAvailabilityChange = useCallback(
    (newIsAvailable: boolean) => {
      setAvailability(newIsAvailable);
      setIsAvailabilityManuallyChanged(true);
      dispatch(
        getCodes({
          page,
          filterValue: availability as FilterValues,
          searchName: codeName ?? "",
        })
      );
    },
    [dispatch, page, availability, codeName]
  );
  const isToBeCanceled =
    state === orderStateStatus.processing ||
    state === orderStateStatus.canceled ||
    state === orderStateStatus.pending;

  return (
    <article className={`${rootClassName} ${additionalClassName || ""}`}>
      <Image link={link} name={__(name)} image={image} />
      <div className={classes.container}>
        <div className={classes.contentContainer}>
          {!isAvailable && (
            <div className={classes.contentContainerUnavailable}>
              {__("Kod wykorzystany")}{" "}
              <CheckIcon className={classes.checkIcon} aria-hidden />
            </div>
          )}
          <Content
            link={link}
            name={__(name)}
            expirationDate={expirationDate}
            options={options}
            dataT1={`${dataT1}_content`}
            codeDetails={codeDetails}
            producSuperOptions={producSuperOptions}
            statusCode={statusCode}
            codeAttributeListSize={codeAttributeListSize}
            label={label}
          />
        </div>
        <div className={classes.buttonsContainer}>
          {!isAvailable ? (
            <button
              type="button"
              className={classes.markAsUnusedButton}
              onClick={markAsUnused}
            >
              {__("Oznacz jako niewykorzystany")}
            </button>
          ) : null}
          <ShowCode
            isCanceled={!!isCanceled || !!isToBeCanceled}
            name={__(name)}
            itemId={itemId}
            orderStatusCode={orderStatusCode}
            sku={sku}
            isShowCodeDisabled={!!disabled}
            product_expiration_date={expirationDate}
            userUsedDate={userUsedDate}
            userUsed={!isAvailable}
            productCodeFrontendStatus={productCodeFrontendStatus}
            onAvailabilityChange={onAvailabilityChange}
          />
        </div>
      </div>
      <Loader isLoading={isSettingMark} />
    </article>
  );
};

export default CodeItem;
